import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Zap, Award, Shield, Heart, TrendingUp, Users } from 'lucide-react';
import AnimatedBackground from '../components/AnimatedBackground';
import {
  GYM_INFO,
  SUPPLEMENTS,
  MEMBERSHIPS,
  WHY_CHOOSE_US,
  TESTIMONIALS,
  GYM_IMAGES,
  TRAINING_IMAGES,
} from '../mock';

const Home = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-black">
      <AnimatedBackground />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
        {/* Hero Background Image with Overlay */}
        <div className="absolute inset-0">
          <img
            src={GYM_IMAGES.heroGym}
            alt="Iron Crust Gym"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black via-black/80 to-transparent" />
          <div className="absolute inset-0 bg-black/40" />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 container mx-auto px-6">
          <div className="max-w-3xl">
            <div className="inline-block mb-6 px-4 py-2 bg-[#39FF14]/20 border border-[#39FF14] rounded-full backdrop-blur-sm">
              <span className="text-[#39FF14] font-bold text-sm uppercase tracking-wider">
                Vijay Nagar, Indore
              </span>
            </div>

            <h1 className="text-6xl md:text-8xl font-black text-white mb-6 uppercase leading-none tracking-tight">
              Build Real <span className="text-[#39FF14]">Strength</span>
            </h1>

            <p className="text-xl md:text-2xl text-gray-300 mb-8 leading-relaxed max-w-2xl">
              Elite coaching • Imported equipment • Authentic supplements • Life-changing results
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/memberships"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#39FF14] text-black font-bold text-lg uppercase rounded-full hover:scale-105 hover:shadow-2xl hover:shadow-[#39FF14]/50 transition-all duration-300"
              >
                Join the Gym
                <ArrowRight className="w-5 h-5" />
              </Link>
              <Link
                to="/supplements"
                className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-transparent text-white font-bold text-lg uppercase rounded-full border-2 border-[#39FF14] hover:bg-[#39FF14]/10 transition-all duration-300"
              >
                Shop Supplements
              </Link>
            </div>

            {/* Trust Indicators */}
            <div className="mt-12 flex flex-wrap items-center gap-8">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#39FF14]" />
                <span className="text-gray-400 text-sm">FSSAI Certified</span>
              </div>
              <div className="flex items-center gap-2">
                <Award className="w-5 h-5 text-[#39FF14]" />
                <span className="text-gray-400 text-sm">Lab Tested</span>
              </div>
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-[#39FF14]" />
                <span className="text-gray-400 text-sm">500+ Transformations</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-[#39FF14] rounded-full p-1">
            <div className="w-1.5 h-3 bg-[#39FF14] rounded-full mx-auto animate-pulse" />
          </div>
        </div>
      </section>

      {/* Why Choose Iron Crust */}
      <section className="py-24 relative">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black text-white mb-4 uppercase">
              Why <span className="text-[#39FF14]">Iron Crust</span>?
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Elite fitness destination in Indore with everything you need to succeed
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {WHY_CHOOSE_US.map((item, index) => (
              <div
                key={item.id}
                className="group relative bg-gradient-to-br from-[#1a1a1a] to-black p-8 rounded-2xl border border-[#39FF14]/20 hover:border-[#39FF14] transition-all duration-300 hover:scale-105"
              >
                <div className="absolute inset-0 bg-[#39FF14]/5 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="relative">
                  <div className="w-12 h-12 bg-[#39FF14]/20 rounded-lg flex items-center justify-center mb-4 group-hover:bg-[#39FF14] transition-colors duration-300">
                    <Zap className="w-6 h-6 text-[#39FF14] group-hover:text-black" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2 uppercase">
                    {item.title}
                  </h3>
                  <p className="text-gray-400">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Programs Teaser */}
      <section className="py-24 relative">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black text-white mb-4 uppercase">
              Our <span className="text-[#39FF14]">Programs</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Transform your body with expert-designed training programs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Personal Training Card */}
            <Link
              to="/personal-training"
              className="group relative overflow-hidden rounded-2xl border border-[#39FF14]/20 hover:border-[#39FF14] transition-all duration-300 hover:scale-105"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={TRAINING_IMAGES.personalTraining}
                  alt="Personal Training"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-2xl font-bold text-white mb-2 uppercase">
                  Personal Training
                </h3>
                <p className="text-gray-300 mb-4">1-on-1 coaching for maximum results</p>
                <span className="inline-flex items-center gap-2 text-[#39FF14] font-bold">
                  Learn More <ArrowRight className="w-5 h-5" />
                </span>
              </div>
            </Link>

            {/* Strength Programs Card */}
            <Link
              to="/strength-programs"
              className="group relative overflow-hidden rounded-2xl border border-[#39FF14]/20 hover:border-[#39FF14] transition-all duration-300 hover:scale-105"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={GYM_IMAGES.equipmentArea}
                  alt="Strength Programs"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-2xl font-bold text-white mb-2 uppercase">
                  Strength Programs
                </h3>
                <p className="text-gray-300 mb-4">Build power & muscle mass</p>
                <span className="inline-flex items-center gap-2 text-[#39FF14] font-bold">
                  Learn More <ArrowRight className="w-5 h-5" />
                </span>
              </div>
            </Link>

            {/* Fat Loss Card */}
            <Link
              to="/fat-loss"
              className="group relative overflow-hidden rounded-2xl border border-[#39FF14]/20 hover:border-[#39FF14] transition-all duration-300 hover:scale-105"
            >
              <div className="aspect-[4/3] overflow-hidden">
                <img
                  src={TRAINING_IMAGES.athleteTraining}
                  alt="Fat Loss Programs"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
              </div>
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <h3 className="text-2xl font-bold text-white mb-2 uppercase">
                  Fat Loss Programs
                </h3>
                <p className="text-gray-300 mb-4">Get lean & shredded</p>
                <span className="inline-flex items-center gap-2 text-[#39FF14] font-bold">
                  Learn More <ArrowRight className="w-5 h-5" />
                </span>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* Featured Supplements */}
      <section className="py-24 relative">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black text-white mb-4 uppercase">
              Premium <span className="text-[#39FF14]">Supplements</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              100% authentic, lab-tested supplements for real gains
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {SUPPLEMENTS.slice(0, 6).map((product) => (
              <div
                key={product.id}
                className="group bg-gradient-to-br from-[#1a1a1a] to-black p-6 rounded-2xl border border-[#39FF14]/20 hover:border-[#39FF14] transition-all duration-300 hover:scale-105"
              >
                <div className="aspect-square mb-4 rounded-xl overflow-hidden bg-black/50">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain p-4 group-hover:scale-110 transition-transform duration-500"
                  />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{product.name}</h3>
                <ul className="space-y-1 mb-4">
                  {product.benefits.slice(0, 3).map((benefit, idx) => (
                    <li key={idx} className="text-sm text-gray-400 flex items-center gap-2">
                      <span className="w-1.5 h-1.5 bg-[#39FF14] rounded-full" />
                      {benefit}
                    </li>
                  ))}
                </ul>
                <div className="flex items-center justify-between">
                  <span className="text-2xl font-bold text-[#39FF14]">₹{product.price}</span>
                  <a
                    href={`${GYM_INFO.whatsappLink}?text=Hi, I want to order ${product.name}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-[#39FF14] text-black font-bold text-sm uppercase rounded-full hover:scale-105 transition-transform"
                  >
                    Order
                  </a>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/supplements"
              className="inline-flex items-center gap-2 px-8 py-4 bg-transparent text-white font-bold text-lg uppercase rounded-full border-2 border-[#39FF14] hover:bg-[#39FF14]/10 transition-all duration-300"
            >
              View All Supplements
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* Membership Highlight */}
      <section className="py-24 relative">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black text-white mb-4 uppercase">
              Membership <span className="text-[#39FF14]">Plans</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Choose the perfect plan for your fitness journey
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {MEMBERSHIPS.map((plan) => (
              <div
                key={plan.id}
                className={`relative bg-gradient-to-br from-[#1a1a1a] to-black p-8 rounded-2xl border-2 transition-all duration-300 hover:scale-105 ${
                  plan.badge ? 'border-[#39FF14]' : 'border-[#39FF14]/20 hover:border-[#39FF14]'
                }`}
              >
                {plan.badge && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#39FF14] text-black font-bold text-sm uppercase rounded-full">
                    {plan.badge}
                  </div>
                )}
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-white mb-2 uppercase">{plan.name}</h3>
                  <div className="text-4xl font-black text-[#39FF14] mb-1">₹{plan.price}</div>
                  <p className="text-gray-400">{plan.duration}</p>
                </div>
                <ul className="space-y-3 mb-8">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-3 text-gray-300">
                      <div className="w-5 h-5 bg-[#39FF14]/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <div className="w-2 h-2 bg-[#39FF14] rounded-full" />
                      </div>
                      {feature}
                    </li>
                  ))}
                </ul>
                <Link
                  to="/memberships"
                  className="block w-full px-6 py-3 bg-[#39FF14] text-black font-bold text-center uppercase rounded-full hover:scale-105 transition-transform"
                >
                  Join Now
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 relative">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-5xl md:text-6xl font-black text-white mb-4 uppercase">
              Real <span className="text-[#39FF14]">Results</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Hear from our successful members
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {TESTIMONIALS.slice(0, 4).map((testimonial) => (
              <div
                key={testimonial.id}
                className="bg-gradient-to-br from-[#1a1a1a] to-black p-8 rounded-2xl border border-[#39FF14]/20 hover:border-[#39FF14] transition-all duration-300"
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <span key={i} className="text-[#39FF14] text-xl">★</span>
                  ))}
                </div>
                <p className="text-gray-300 mb-6 leading-relaxed">{testimonial.text}</p>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-white font-bold">{testimonial.name}</p>
                    <p className="text-[#39FF14] text-sm">{testimonial.result}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/reviews"
              className="inline-flex items-center gap-2 px-8 py-4 bg-transparent text-white font-bold text-lg uppercase rounded-full border-2 border-[#39FF14] hover:bg-[#39FF14]/10 transition-all duration-300"
            >
              View All Reviews
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={GYM_IMAGES.sunsetCardio}
            alt="Join Iron Crust"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/80" />
        </div>
        <div className="relative z-10 container mx-auto px-6 text-center">
          <h2 className="text-5xl md:text-7xl font-black text-white mb-6 uppercase">
            Ready to <span className="text-[#39FF14]">Transform</span>?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Join Iron Crust Gym today and start your journey to the best version of yourself
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`${GYM_INFO.whatsappLink}?text=Hi, I want to join Iron Crust Gym!`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-[#39FF14] text-black font-bold text-lg uppercase rounded-full hover:scale-105 hover:shadow-2xl hover:shadow-[#39FF14]/50 transition-all duration-300"
            >
              Join on WhatsApp
              <ArrowRight className="w-5 h-5" />
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-transparent text-white font-bold text-lg uppercase rounded-full border-2 border-white hover:bg-white/10 transition-all duration-300"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
